/**
 * Sprint-104d-full — useOrderRealtime.
 *
 * 15-second polling hook for the customer tracking page. Polls
 *   • GET /api/orders/{trackingId}
 *   • GET /api/orders/{trackingId}/eta
 * concurrently and returns the latest snapshot.
 *
 * Also exposes a WS-ready facade: `subscribe(handler)` /
 * `unsubscribe(handler)` no-ops that Phase 105-Agent-C can swap for a
 * real WebSocket without rewriting consumers.
 *
 * Audit-log emission: on mount, fires a one-shot fire-and-forget
 * POST /api/orders/{tid}/tracking-viewed — the backend rate-limits to
 * one row per (customer, order, hour).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../services/api";

const DEFAULT_POLL_MS = 15_000;

// Module-scope subscribers map so the WS swap can broadcast to every
// hook consumer the same way the poll-based snapshots do.
const _subscribers = new Map(); // trackingId -> Set<handler>

function _broadcast(trackingId, snapshot) {
  const set = _subscribers.get(trackingId);
  if (!set) return;
  for (const fn of set) {
    try { fn(snapshot); } catch (_) { /* ignore consumer errors */ }
  }
}

export function subscribe(trackingId, handler) {
  if (!_subscribers.has(trackingId)) _subscribers.set(trackingId, new Set());
  _subscribers.get(trackingId).add(handler);
  return () => unsubscribe(trackingId, handler);
}

export function unsubscribe(trackingId, handler) {
  const set = _subscribers.get(trackingId);
  if (set) set.delete(handler);
}

export default function useOrderRealtime(trackingId, { pollMs = DEFAULT_POLL_MS, enabled = true } = {}) {
  const [order, setOrder]         = useState(null);
  const [eta, setEta]             = useState(null);
  const [loading, setLoading]     = useState(true);
  const [errorKind, setErrorKind] = useState(null); // "404" | "403" | "error" | null
  const [refreshedAt, setRefreshedAt] = useState(null);
  const auditFiredRef = useRef(false);
  const timerRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    if (!trackingId) return;
    try {
      const [orderResp, etaResp] = await Promise.allSettled([
        api.get(`/orders/${trackingId}`),
        api.get(`/orders/${trackingId}/eta`),
      ]);

      if (orderResp.status === "fulfilled") {
        setOrder(orderResp.value.data);
        setErrorKind(null);
        _broadcast(trackingId, { order: orderResp.value.data, eta: etaResp.status === "fulfilled" ? etaResp.value.data : null });
      } else {
        const code = orderResp.reason?.response?.status;
        if (code === 404)      setErrorKind("404");
        else if (code === 403) setErrorKind("403");
        else                   setErrorKind("error");
      }
      if (etaResp.status === "fulfilled") {
        setEta(etaResp.value.data);
      }
      setRefreshedAt(new Date().toISOString());
    } catch (e) {
      setErrorKind("error");
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  // One-shot audit-log emission. Fire-and-forget — backend rate-limits.
  useEffect(() => {
    if (!trackingId || auditFiredRef.current) return;
    auditFiredRef.current = true;
    api.post(`/orders/${trackingId}/tracking-viewed`).catch(() => { /* swallow */ });
  }, [trackingId]);

  // Initial load + interval.
  useEffect(() => {
    if (!enabled || !trackingId) return;
    setLoading(true);
    fetchOnce();
    if (pollMs > 0) {
      timerRef.current = setInterval(fetchOnce, pollMs);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }
    return undefined;
  }, [trackingId, enabled, pollMs, fetchOnce]);

  return {
    order,
    eta,
    loading,
    errorKind,
    refreshedAt,
    refresh: fetchOnce,
    // WS-ready facade — Phase 105-Agent-C swaps these.
    subscribe: (handler) => subscribe(trackingId, handler),
    unsubscribe: (handler) => unsubscribe(trackingId, handler),
  };
}
